import axios from 'axios';
import * as Sentry from '@sentry/react';
import type { ConsumableType, Consumable, ConsumableBase } from './consumableService';

const authHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export type ProcurementSource = 'manual' | 'xero';

export interface Procurement {
  _id: string;
  tenantId: string;
  // Backend uses a tenant-scoped populate (`populate({ path: 'productId', match: { tenantId } })`).
  // When the referenced Consumable no longer exists in the tenant (orphan transaction) the
  // populated value is `null` rather than the Consumable document. Callers MUST guard.
  productId: Consumable | null;
  type: 'purchase';
  quantity: number;
  date: string;
  supplier: string;
  unitPrice: number;
  totalPrice: number;
  referenceNumber: string;
  source: ProcurementSource;
  xeroInvoiceId?: string;
  expiryDate?: string;
  quantityUnit: string;
  createdBy: string;
  createdAt: string;
  attachments?: Array<{
    _id: string;
    s3Key: string;
    fileName: string;
    mimeType: string;
    label?: 'invoice' | 'delivery_note' | 'other';
    uploadedAt: string;
  }>;
}

export interface ListProcurementParams {
  from?: string;
  to?: string;
  supplier?: string;
  consumableId?: string;
  consumableType?: ConsumableType;
  source?: ProcurementSource;
  limit?: number;
  cursor?: string;
}

export async function listProcurement(params: ListProcurementParams = {}): Promise<{ rows: Procurement[]; nextCursor: string | null }> {
  const q = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => { if (v != null) q.set(k, String(v)); });
  const url = `/api/v1/consumables/procurement${q.toString() ? `?${q}` : ''}`;
  try {
    const res = await axios.get<{ rows: Procurement[]; nextCursor: string | null }>(url, { headers: authHeaders() });
    return res.data;
  } catch (err) {
    Sentry.captureException(err);
    throw err;
  }
}

export async function getProcurement(id: string): Promise<Procurement> {
  try {
    const res = await axios.get<Procurement>(`/api/v1/consumables/procurement/${id}`, { headers: authHeaders() });
    return res.data;
  } catch (err) {
    Sentry.captureException(err);
    throw err;
  }
}

export interface AttachmentSignedUrl {
  signedUrl: string;
  fileName: string;
  mimeType: string;
  label?: 'invoice' | 'delivery_note' | 'other';
}

export async function getProcurementAttachmentSignedUrl(
  id: string,
  attachmentId: string,
): Promise<AttachmentSignedUrl> {
  try {
    const res = await axios.get<AttachmentSignedUrl>(
      `/api/v1/consumables/procurement/${id}/attachment/${attachmentId}/signed-url`,
      { headers: authHeaders() },
    );
    return res.data;
  } catch (err) {
    Sentry.captureException(err);
    throw err;
  }
}

export interface CreateProcurementBody {
  productId: string;
  date: string;
  supplier: string;
  quantity: number;
  quantityUnit: string;
  unitPrice: number;
  totalPrice: number;
  referenceNumber: string;
  source: 'manual';
  expiryDate?: string;
  attachments?: UploadedAttachment[];
}

export async function createProcurement(body: CreateProcurementBody): Promise<Procurement> {
  try {
    const res = await axios.post<Procurement>('/api/v1/consumables/procurement', body, { headers: authHeaders() });
    return res.data;
  } catch (err) {
    Sentry.captureException(err);
    throw err;
  }
}

// ── Attachments (Spec B Task 7.4) ───────────────────────────────────────────
// Uploaded BEFORE the procurement row is created. The form bundles the
// returned metadata into the POST /procurement body, where it is persisted
// on the ConsumableTransaction's `attachments[]` sub-document.
export interface UploadedAttachment {
  s3Key: string;
  fileName: string;
  mimeType: string;
  uploadedAt: string;
}

export async function uploadProcurementAttachment(file: File): Promise<UploadedAttachment> {
  const form = new FormData();
  form.append('file', file);
  try {
    const res = await axios.post<UploadedAttachment>(
      '/api/v1/consumables/procurement/attachment',
      form,
      {
        headers: {
          ...authHeaders(),
          'Content-Type': 'multipart/form-data',
        },
      },
    );
    return res.data;
  } catch (err) {
    Sentry.captureException(err);
    throw err;
  }
}

export interface QuickCreateConsumableBody {
  consumableType: ConsumableType;
  productName: string;
  category?: string;
}

export async function quickCreateConsumable(body: QuickCreateConsumableBody): Promise<ConsumableBase> {
  try {
    const res = await axios.post<ConsumableBase>('/api/v1/consumables/quick-create', body, { headers: authHeaders() });
    return res.data;
  } catch (err) {
    Sentry.captureException(err);
    throw err;
  }
}

// Xero mock — preview + import
export interface XeroInvoicePayload {
  InvoiceID: string;
  InvoiceNumber: string;
  Date: string;
  Contact: { Name: string };
  LineItems: Array<{ Description: string; Quantity: number; UnitAmount: number; LineAmount: number }>;
  Total: number;
}

export async function getXeroPreview(): Promise<XeroInvoicePayload> {
  try {
    const res = await axios.get<XeroInvoicePayload>('/api/v1/consumables/procurement/xero/preview', { headers: authHeaders() });
    return res.data;
  } catch (err) {
    Sentry.captureException(err);
    throw err;
  }
}

export interface XeroImportResult {
  transactions: Procurement[];
  draftProductsCreated: ConsumableBase[];
}

export async function importXero(payload: XeroInvoicePayload): Promise<XeroImportResult> {
  try {
    const res = await axios.post<XeroImportResult>('/api/v1/consumables/procurement/xero/import', payload, { headers: authHeaders() });
    return res.data;
  } catch (err) {
    Sentry.captureException(err);
    throw err;
  }
}
